import {toastError, toastSuccess} from "../../component/shared/toast/Toast";
import i18n from "../../config/i18n";
import WishService from "../../api/wishService";
import {ICitiesResponse, WishSortOptions, WishStatus, WishType} from "./Helper";
import {ICreateEditWish} from "../../component/admin-panel/wishes/create-edit-wish-dialog/CreateEditWishDialogHelper";
import {RootState} from "../index";
import {getSafeBackendErrorMessage} from "../../util/utils";
import {setApplicationError} from "../app/Slice";
import {createAsyncThunk} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {
    IEditActiveWish
} from "../../component/admin-panel/wishes/wishes-table/edit-active-wish-dialog/EditActiveWishDialogHelper";

export const getProfileWishes =
    createAsyncThunk(`${SliceNames.WISH}/getProfileWishes`,
        async (req: {
            category_id: string | undefined,
            searchText: string,
            status: WishStatus,
            sort_by?: string,
            sort_order?: string,
            type?: WishType
        }, thunkAPI) => {
            try {
                const res = await WishService.fetchMyWishes({
                    wish_status: req.status,
                    sort_order: req.sort_order,
                    sort_by: req.sort_by,
                    search_text: req.searchText,
                    category_id: req.category_id,
                    type: req.type
                })
                return {data: res.data, status: req.status.toLowerCase()}
            } catch (e: any) {
                thunkAPI.dispatch(
                    setApplicationError({
                        isError: true,
                        statusCode: undefined
                    }))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const getWishes =
    createAsyncThunk(`${SliceNames.WISH}/getWishes`,
        async (req: {
            category_id?: string,
            sorting: string,
            page: number,
            searchText?: string,
            status?: WishStatus,
            limit: number,
            loadMore: boolean,
            recommendedFirst?: boolean
            clientId?: string,
            type?: WishType,
            city?: string,
            forAdminPanel?: boolean
        }, thunkAPI) => {
            try {
                const sortParts = req.sorting.split(',')
                const res = await WishService.fetchWishes(
                    {
                        wish_status: req.status,
                        limit: req.limit,
                        offset: req.page,
                        sort_by: sortParts[0],
                        sort_order: sortParts[1],
                        search_text: req.searchText,
                        category_id: req.category_id,
                        recommended_first: req.recommendedFirst,
                        client_id: req.clientId,
                        type: req.type,
                        city: req.city,
                        for_admin_panel: req.forAdminPanel
                    }
                )
                const oldWishes = (thunkAPI.getState() as RootState).wishReducer.items.data
                if (req.loadMore) {
                    res.data.data = [...oldWishes, ...res.data.data]
                }
                return res.data
            } catch (e: any) {
                thunkAPI.dispatch(
                    setApplicationError({
                        isError: true,
                        statusCode: undefined
                    }))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const getPastWinners =
    createAsyncThunk(`${SliceNames.WISH}/getPastWinners`,
        async (_, thunkAPI) => {
            try {
                const res = await WishService.fetchWishes(
                    {
                        wish_status: WishStatus.FINISHED,
                        limit: 20,
                        offset: 0,
                        sort_by: WishSortOptions.END_TIME,
                        sort_order: "desc",
                        recommended_first: false,
                        type: WishType.DRAW
                    })
                return res.data.data
            } catch (e: any) {
                thunkAPI.dispatch(
                    setApplicationError({
                        isError: true,
                        statusCode: undefined
                    }))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const getWishDetails =
    createAsyncThunk(`${SliceNames.WISH}/getWishDetails`,
        async (wishId: string, thunkAPI) => {
            try {
                const res = await WishService.fetchWishDetails(wishId)
                return res.data
            } catch (e: any) {
                thunkAPI.dispatch(
                    setApplicationError({
                        isError: true,
                        statusCode: e.response?.status
                    }))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const getWishImages =
    createAsyncThunk(`${SliceNames.WISH}/getWishImages`,
        async (wishId: string, thunkAPI) => {
            try {
                const res = await WishService.fetchWishImages(wishId)
                return res.data
            } catch (e: any) {
                return thunkAPI.rejectWithValue(e)
            }
        })

export const createWish =
    createAsyncThunk(`${SliceNames.WISH}/createWish`,
        async (req: {
            body: ICreateEditWish,
            setActiveStep: (step: number) => void,
            setCreatedWishId: (wishId: string) => void
        }, thunkAPI) => {
            try {
                const res = await WishService.createWish(req.body)
                toastSuccess(i18n.t('changes_saved_success'))
                req.setCreatedWishId(res.data.id)
                req.setActiveStep(1)
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const deleteWish =
    createAsyncThunk(`${SliceNames.WISH}/deleteWish`,
        async (req: {
            id: string,
            handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                await WishService.deleteWish(req.id)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleDialogClose()
                return req.id
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const publishWish =
    createAsyncThunk(`${SliceNames.WISH}/publishWish`,
        async (req: {
            id: string,
            handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                await WishService.publishWish(req.id)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleDialogClose()
                return req.id
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const terminateWish =
    createAsyncThunk(`${SliceNames.WISH}/terminateWish`,
        async (req: {
            id: string,
            handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                await WishService.terminateWish(req.id)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleDialogClose()
                return req.id
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const rescheduleWish =
    createAsyncThunk(`${SliceNames.WISH}/rescheduleWish`,
        async (req: {
            id: string,
            handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                await WishService.rescheduleWish(req.id)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleDialogClose()
                return req.id
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const editWish =
    createAsyncThunk(`${SliceNames.WISH}/editWish`,
        async (req: {
            id: string, body: ICreateEditWish | IEditActiveWish, resolveFunction: () => void
        }, thunkAPI) => {
            try {
                const res = await WishService.editWish(req.id, req.body)
                toastSuccess(i18n.t('changes_saved_success'))
                req.resolveFunction()

                return res.data
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const uploadImages =
    createAsyncThunk(`${SliceNames.WISH}/uploadImages`,
        async (req: {
            wishId: string, imagesToAdd: File[], imagesToRemove: string[], thumbnail: string, handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                const formData: any = new FormData()
                for (const image of req.imagesToAdd)
                    formData.append('images_to_add', image);
                formData.append('images_to_remove', JSON.stringify(req.imagesToRemove))
                formData.append('thumbnail', req.thumbnail)
                await WishService.uploadImages(req.wishId, formData)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleDialogClose()
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })


export const getWishRulebook =
    createAsyncThunk(`${SliceNames.WISH}/getWishRulebook`,
        async (wishId: string, thunkAPI) => {
            try {
                const res = await WishService.fetchWishRulebook(wishId)
                return res.data
            } catch (e: any) {
                // toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const updateRulebook =
    createAsyncThunk(`${SliceNames.WISH}/updateRulebook`,
        async (req: {
            wishId: string, rulebook: File, handleCloseDialog: () => void
        }, thunkAPI) => {
            try {
                const res = await WishService.updateWishRulebook(req.wishId, req.rulebook)
                toastSuccess(i18n.t('update_rulebook_success'))
                req.handleCloseDialog()
                return res.data
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const redrawWinner =
    createAsyncThunk(`${SliceNames.WISH}/redrawWinner`,
        async (req: {
            id: string,
            handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                await WishService.redrawWinner(req.id)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleDialogClose()
                return req.id
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const duplicateWish =
    createAsyncThunk(`${SliceNames.WISH}/duplicateWish`,
        async (req: {
            wishId: string, handleCloseDialog: () => void
        }, thunkAPI) => {
            try {
                await WishService.duplicateWish(req.wishId)
                toastSuccess(i18n.t('changes_saved_success'))
                req.handleCloseDialog()
            } catch (e: any) {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const getCitiesWithCounts =
    createAsyncThunk(`${SliceNames.WISH}/getCitiesWithCounts`,
        async (req: { type?: WishType, sorted?: boolean } = {sorted: false}, thunkAPI) => {
            try {
                const res = await WishService.fetchCitiesWithCounts(req.type)
                if (req.sorted) {
                    return res.data.sort((a: ICitiesResponse, b: ICitiesResponse) => b.count - a.count);
                }

                return res.data
            } catch (e: any) {
                thunkAPI.dispatch(setApplicationError({isError: true, statusCode: undefined}))
                return thunkAPI.rejectWithValue(e)
            }
        })